// Assessment module interfaces
//
// OpenAPI reconciliation (`docs/api-reference/openapi-spec.json`, academics tag):
//
// **Class assessments** (`GET|POST /v1/academics/assessments`, `GET …/assessments/{id}`):
// - List rows use `GetAssessmentResponse`; detail/create use `AssessmentResponse` — same wire fields; FE models both as {@link TGetAcademicAssessmentResponse}.
// - {@link TPaginatedAcademicAssessments} matches `PaginatedAssessmentsWireDto` (`data` + `count`).
// - `CreateAssessmentRequest.assessmentType` is documented as DB strings: exam, test, assignment, project, quiz, mid_term, final, unit_test, practical — see {@link ACADEMIC_ASSESSMENT_TYPE_VALUES}.
// - List/create `status` enum: draft, scheduled, in_progress, completed, graded, cancelled — see {@link ACADEMIC_ASSESSMENT_STATUS_VALUES}. Legacy UI used `active`; normalize to `in_progress` when reading wire data.
// - `GET /v1/academics/assessments/classes` is listed in OpenAPI but response schema is not fully specified; FE types the wire as {@link TAcademicAssessmentsClassesWireResponse}.
// - **Divergence:** `PATCH /v1/academics/assessments/{id}` is not in the published OpenAPI document; the service calls it for live updates (common Nest partial-update pattern). Mock mode implements update in-process. If the BE returns 404/405, add the route to the API or keep mock-only edits.
//
// **Legacy `/v1/assessment/*`** (frameworks, scoring structures, grade criteria, evaluation frameworks): FE types are product-specific; those paths are not under `academics` in OpenAPI — use mocks or a separate BE contract.

export type TAssessmentFrameworkStatus = "active" | "inactive";
export type TAssessmentType =
  | "continuous-assessment"
  | "term-exams"
  | "project-based"
  | "practical"
  | "oral"
  | "written"
  | "other";

export type TAssessmentFramework = {
  id: string;
  name: string;
  classId: string;
  className?: string; // For display purposes
  assessmentType: TAssessmentType;
  description?: string;
  status: TAssessmentFrameworkStatus;
  instituteId: string;
  createdAt?: string;
  updatedAt?: string;
};

export type TCreateAssessmentFrameworkRequest = {
  name: string;
  classId: string;
  assessmentType: TAssessmentType;
  description?: string;
  status?: TAssessmentFrameworkStatus;
  instituteId: string;
};

export type TUpdateAssessmentFrameworkRequest = {
  name?: string;
  classId?: string;
  assessmentType?: TAssessmentType;
  description?: string;
  status?: TAssessmentFrameworkStatus;
};

/** OpenAPI `GET /v1/academics/assessments` filter — wire status values. */
export type TAcademicAssessmentListStatus =
  | "draft"
  | "scheduled"
  | "in_progress"
  | "completed"
  | "graded"
  | "cancelled";

/** Values aligned with OpenAPI `CreateAssessmentRequest.assessmentType` description. */
export const ACADEMIC_ASSESSMENT_TYPE_VALUES = [
  "exam",
  "test",
  "assignment",
  "project",
  "quiz",
  "mid_term",
  "final",
  "unit_test",
  "practical",
] as const;

export type TAcademicAssessmentWireType =
  (typeof ACADEMIC_ASSESSMENT_TYPE_VALUES)[number];

/** @deprecated Prefer {@link TAcademicAssessmentWireType}. */
export type TAcademicAssessmentCreateType = TAcademicAssessmentWireType;

export const ACADEMIC_ASSESSMENT_STATUS_VALUES = [
  "draft",
  "scheduled",
  "in_progress",
  "completed",
  "graded",
  "cancelled",
] as const;

export type TAcademicAssessmentWireStatus =
  (typeof ACADEMIC_ASSESSMENT_STATUS_VALUES)[number];

export type TAcademicAssessmentCreateStatus = TAcademicAssessmentWireStatus;

export type TAcademicAssessmentRef = {
  id?: string;
  name?: string;
};

export type TGetAcademicAssessmentResponse = {
  id: string;
  name: string;
  description?: string;
  assessmentType: string;
  status: string;
  class?: TAcademicAssessmentRef | Record<string, unknown>;
  course?: TAcademicAssessmentRef | Record<string, unknown>;
  maxScore: number;
  totalStudents?: number;
  averageScore?: number;
  averagePercentage?: number;
  scheduledDate?: string;
  conductedDate?: string;
  dueDate?: string;
  gradedDate?: string;
  weightage?: number;
  instructions?: string;
  remarks?: string;
  academicYear?: string;
  institute?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type TPaginatedAcademicAssessments = {
  data: TGetAcademicAssessmentResponse[];
  count: number;
};

export type TAcademicAssessmentListQuery = {
  page?: number;
  limit?: number;
  instituteId?: string;
  classId?: string;
  /** Wire name unchanged; value is the institute subject id (BE §2.7). */
  courseId?: string;
  academicYear?: string;
  assessmentType?: string;
  status?: TAcademicAssessmentListStatus;
};

/** Query for `GET /v1/academics/assessments/classes`. */
export type TAcademicAssessmentsClassesQuery = Pick<
  TAcademicAssessmentListQuery,
  "instituteId" | "academicYear" | "assessmentType" | "status"
>;

export type TCreateAcademicAssessmentRequest = {
  instituteId: string;
  name: string;
  assessmentType: string;
  maxScore: number;
  description?: string;
  status?: TAcademicAssessmentCreateStatus;
  classId?: string;
  courseId?: string;
  scheduledDate?: string;
  dueDate?: string;
  weightage?: number;
  instructions?: string;
  academicYear?: string;
  createdBy?: string;
};

/** Partial update (OpenAPI omits this schema — mirrors create fields omitting `instituteId`). */
export type TUpdateAcademicAssessmentRequest = Omit<
  Partial<TCreateAcademicAssessmentRequest>,
  "instituteId"
>;

/**
 * Wire shape for `GET /v1/academics/assessments/classes` when the backend returns keyed class groups.
 * OpenAPI leaves the response body unspecified; widen as the BE stabilizes.
 */
export type TAcademicAssessmentsClassesWireResponse = Record<
  string,
  unknown
>;

/** @deprecated Use {@link TGetAcademicAssessmentResponse}; kept for mock file imports. */
export type TAssessment = TGetAcademicAssessmentResponse;

// Story 7.3: Scoring Structure Configuration per Class
export type TAssessmentComponent = {
  id: string;
  name: string;
  weightPercentage: number; // Percentage of total marks (e.g., 20 for 20%)
  marks?: number; // Optional: specific marks if not using percentage
};

export type TScoringStructure = {
  id: string;
  classId: string;
  className?: string; // For display purposes
  frameworkId?: string; // Optional: link to assessment framework
  totalMarks: number;
  passingMarks: number;
  components: TAssessmentComponent[];
  instituteId: string;
  createdAt?: string;
  updatedAt?: string;
};

export type TCreateScoringStructureRequest = {
  classId: string;
  frameworkId?: string;
  totalMarks: number;
  passingMarks: number;
  components: Omit<TAssessmentComponent, "id">[];
  instituteId: string;
};

export type TUpdateScoringStructureRequest = {
  totalMarks?: number;
  passingMarks?: number;
  components?: Omit<TAssessmentComponent, "id">[];
};

// Story 7.4: Grade/Assessment Criteria Setup
export type TAssessmentRubric = {
  id: string;
  assessmentType: TAssessmentType;
  criteria: string;
  description?: string;
};

export type TGradeCriteria = {
  id: string;
  gradeLevelId: string;
  gradeLevelName?: string; // For display purposes
  minMarks: number;
  maxMarks?: number; // Optional: if not specified, it's the upper bound
  description?: string;
  performanceLevel?: string; // e.g., "Excellent", "Good", "Satisfactory"
  rubrics?: TAssessmentRubric[];
  instituteId: string;
  createdAt?: string;
  updatedAt?: string;
};

export type TCreateGradeCriteriaRequest = {
  gradeLevelId: string;
  minMarks: number;
  maxMarks?: number;
  description?: string;
  performanceLevel?: string;
  rubrics?: Omit<TAssessmentRubric, "id">[];
  instituteId: string;
};

export type TUpdateGradeCriteriaRequest = {
  minMarks?: number;
  maxMarks?: number;
  description?: string;
  performanceLevel?: string;
  rubrics?: Omit<TAssessmentRubric, "id">[];
};

// Story 7.5: Evaluation Framework Configuration
export type TEvaluationModel =
  | "semester-based"
  | "annual"
  | "continuous-evaluation";
export type TEvaluationFrequency =
  | "weekly"
  | "bi-weekly"
  | "monthly"
  | "quarterly"
  | "semesterly"
  | "annually";

export type TEvaluationFrameworkComponent = {
  id: string;
  name: string;
  description?: string;
  weightPercentage?: number;
};

export type TEvaluationFramework = {
  id: string;
  name: string;
  classIds: string[]; // Can be applied to multiple classes
  className?: string[]; // For display purposes
  evaluationModel: TEvaluationModel;
  evaluationFrequency: TEvaluationFrequency;
  assessmentComponents: TEvaluationFrameworkComponent[];
  gradingMethodology?: string; // Description of grading approach
  gradeLevelIds: string[]; // References to grade levels from academic structure
  gradeLevelNames?: string[]; // For display purposes
  description?: string;
  instituteId: string;
  createdAt?: string;
  updatedAt?: string;
};

export type TCreateEvaluationFrameworkRequest = {
  name: string;
  classIds: string[];
  evaluationModel: TEvaluationModel;
  evaluationFrequency: TEvaluationFrequency;
  assessmentComponents: Omit<TEvaluationFrameworkComponent, "id">[];
  gradingMethodology?: string;
  gradeLevelIds: string[];
  description?: string;
  instituteId: string;
};

export type TUpdateEvaluationFrameworkRequest = {
  name?: string;
  classIds?: string[];
  evaluationModel?: TEvaluationModel;
  evaluationFrequency?: TEvaluationFrequency;
  assessmentComponents?: Omit<TEvaluationFrameworkComponent, "id">[];
  gradingMethodology?: string;
  gradeLevelIds?: string[];
  description?: string;
};
